"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader, CircleCheck } from "lucide-react";
import { cn } from "@/lib/utils";
function RetreatForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 bg-primary/10 border border-primary/20 rounded-lg text-center animate-in fade-in duration-500">
        <CircleCheck className="size-12 text-primary mb-4" />
        <h3 className="text-xl font-serif text-foreground mb-2">Deine Anfrage war erfolgreich</h3>
        <p className="text-muted-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="w-full sm:w-auto">
          Weitere Anfrage senden
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Bitte gib deinen Namen ein."
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Dein Name</FormLabel>
              <FormControl>
                <Input placeholder="Anna Müller" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Bitte gib deine E-Mail-Adresse ein.",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Bitte gib eine gültige E-Mail-Adresse ein."
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Deine E-Mail-Adresse</FormLabel>
              <FormControl>
                <Input type="email" placeholder="anna.mueller@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel>Telefonnummer <span className="text-muted-foreground font-normal text-xs ml-1">(optional)</span></FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+41 79 123 45 67" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="preferred_date" render={({
        field
      }) => <FormItem>
              <FormLabel>Bevorzugtes Datum <span className="text-muted-foreground font-normal text-xs ml-1">(optional)</span></FormLabel>
              <FormControl>
                <Input type="date" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel>Deine Interessen / Fragen <span className="text-muted-foreground font-normal text-xs ml-1">(optional)</span></FormLabel>
            <FormControl>
              <textarea data-slot="input" className={cn("file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex w-full min-w-0 rounded-md border bg-transparent px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm", "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]", "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive", "min-h-[100px] resize-y")} placeholder="Ich interessiere mich für ein Wochenende mit Fokus auf Traumaheilung..." {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="data_privacy_consent" rules={{
      required: "Bitte stimme der Datenschutzerklärung zu."
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 shadow-xs">
            <FormControl>
              <Checkbox checked={field.value} onCheckedChange={field.onChange} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>
                Ich stimme der Verarbeitung meiner Daten gemäss Datenschutzerklärung zu.
              </FormLabel>
            </div>
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto mt-2" size="lg">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Wird gesendet…
          </> : "Auf die Warteliste setzen"}
      </Button>
    </Form>;
}
export default function SwissRetreatExperiences() {
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="schweizer-retreat-erlebnisse" className="dark bg-background text-foreground relative overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[800px]">
        {}
        <div ref={imageRef} className={cn("relative h-[600px] lg:h-auto w-full transition-all duration-1000 ease-out", isImageVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8")}>
          <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1581972/img/alpine_retreat_center.png)] bg-cover bg-center" />
          {}
          <div className="absolute inset-0 bg-background/20 mix-blend-multiply" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent lg:bg-gradient-to-r !bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1581972/img/provence_retreat_landscape_highres.png)]" />
        </div>

        {}
        <div ref={contentRef} className={cn("flex flex-col justify-center px-6 py-16 md:px-12 lg:px-20 xl:px-24 transition-all duration-1000 delay-200 ease-out", isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="max-w-xl">
            <h2 className="text-3xl tracking-tight mb-6 text-foreground !font-sans md:text-4xl lg:text-5xl">LaWandelherz Retreats in der Provence</h2>
            <p className="text-lg text-muted-foreground mb-10 leading-relaxed">Gönne Dir eine Auszeit vom Alltag. Meine zukünftigen Healing Retreats in Südfrankreich, inmitten der wunderschönen Provence, bieten Dir den perfekten Raum, um neue Energie zu tanken, emotionale Klarheit zu finden und dich intensiv mit Deiner inneren Entwicklung zu befassen.</p>

            <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-6 md:p-8 shadow-lg">
              <h3 className="text-xl font-medium mb-6 text-foreground">Deine Anfrage für das Retreat in der Provence.</h3>
              
              <FormProvider formId="retreat_interest_form_home" sectionName="SwissRetreatExperiences" defaultValues={{
              name: "",
              email: "",
              phone: "",
              preferred_date: "",
              message: "",
              data_privacy_consent: false
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              data_privacy_consent: {
                required: true
              }
            }} submitText="Auf die Warteliste setzen" successMessage="Vielen Dank für dein Interesse. Ich habe deine Anfrage erhalten und werde mich in Kürze mit weiteren Details zu meinen Retreats bei dir melden." errorMessage="Es gab ein Problem beim Senden deiner Anfrage. Bitte versuche es später noch einmal.">
                <RetreatForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}